import { useEffect, useState } from 'react';
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Crown, Loader2, LogIn, Pencil, Plus, Search, Send, Trash2, Users, UserMinus, UserPlus, X } from 'lucide-react';
import { toast } from 'sonner';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ImageUpload } from '@/components/ui/image-upload';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useImpersonate } from '@/hooks/users/useImpersonate';
import { useTeamData } from '@/hooks/users/useTeamData';
import { useTeamsList } from '@/hooks/teams/useTeamsList';
import { useUpdateUser } from '@/hooks/users/useUserManagement';
import { ApiService } from '@/services/api/ApiService';
import { AccountBrandingService } from '@/services/branding/AccountBrandingService';
import { UserService } from '@/services/users/UserService';
import { ProfileWithSubscription } from '@/types/auth/auth';
import { DEFAULT_BRANDING, GenieBranding } from '@/types/ui/branding';

interface UserTeamTabProps {
  user: ProfileWithSubscription;
  onUserUpdated: () => void;
}

export const UserTeamTab = ({ user, onUserUpdated }: UserTeamTabProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isRemovingMemberId, setIsRemovingMemberId] = useState<string | null>(null);
  const updateUserMutation = useUpdateUser();
  const { impersonate } = useImpersonate();

  const handleImpersonateForBranding = () => {
    impersonate(user.id, user.full_name || user.email || user.id, "/genie-hub/account-branding");
  };

  const [isCreatingTeam, setIsCreatingTeam] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");
  const [newTeamBrand, setNewTeamBrand] = useState("");
  const [isSavingTeam, setIsSavingTeam] = useState(false);

  const [isEditingTeam, setIsEditingTeam] = useState(false);
  const [editTeamName, setEditTeamName] = useState("");
  const [editTeamBrand, setEditTeamBrand] = useState("");

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeletingTeam, setIsDeletingTeam] = useState(false);

  const [ownerCandidate, setOwnerCandidate] = useState<{ id: string; label: string } | null>(null);
  const [isChangingOwner, setIsChangingOwner] = useState(false);

  const [inviteEmail, setInviteEmail] = useState("");
  const [isInvitingMember, setIsInvitingMember] = useState(false);

  const { data: teamData, isLoading: isTeamLoading, refetch: refetchTeam } = useTeamData(user.team_id, user.team?.owner_id ?? undefined);
  const { data: teamsResult, isLoading: isSearching } = useTeamsList(searchTerm, 1, 20);

  const { data: pendingInvitations, refetch: refetchInvitations } = useQuery({
    queryKey: ["team-invitations", user.team_id],
    queryFn: async () => {
      const response = await ApiService.invoke<{ invitations: { id: string; email: string; invited_at: string }[] }>({
        resource: "teams",
        action: "invitations",
        id: user.team_id as number,
      });
      return response?.invitations ?? [];
    },
    enabled: !!user.team_id,
  });

  const queryClient = useQueryClient();
  const teamId = user.team_id ?? null;
  const { data: branding, isLoading: isBrandingLoading } = useQuery({
    queryKey: ["team-branding", teamId],
    queryFn: () => AccountBrandingService.getAccountBranding(teamId as number),
    enabled: !!teamId,
  });
  const [brandingForm, setBrandingForm] = useState<GenieBranding>(DEFAULT_BRANDING);
  const [isSavingBranding, setIsSavingBranding] = useState(false);

  useEffect(() => {
    setBrandingForm(branding || DEFAULT_BRANDING);
  }, [branding]);

  const handleAssignTeam = async (teamId: number) => {
    try {
      await updateUserMutation.mutateAsync({
        userId: user.id,
        profileUpdates: { team_id: teamId },
      });
      setSearchTerm("");
      onUserUpdated();
    } catch (error) {
      console.error("Error assigning team:", error);
    }
  };

  const handleRemoveFromTeam = async () => {
    try {
      await updateUserMutation.mutateAsync({
        userId: user.id,
        profileUpdates: { team_id: null },
      });
      onUserUpdated();
    } catch (error) {
      console.error("Error removing user from team:", error);
    }
  };

  const handleRemoveMember = async (memberId: string, memberLabel: string) => {
    if (!user.team_id) return;

    setIsRemovingMemberId(memberId);
    try {
      await UserService.removeTeamMember(user.team_id, memberId, user.id);
      toast.success(`${memberLabel} removed from team`);
      await refetchTeam();
    } catch (error: any) {
      console.error("Error removing team member:", error);
      toast.error(error.message || "Failed to remove member");
    } finally {
      setIsRemovingMemberId(null);
    }
  };

  const handleCancelInvitation = async (invitationId: string, email: string) => {
    if (!user.team_id) return;

    setIsRemovingMemberId(invitationId);
    try {
      await UserService.cancelInvitation(invitationId, user.team_id);
      toast.success(`Invitation to ${email} cancelled`);
      await refetchInvitations();
    } catch (error: any) {
      console.error("Error cancelling invitation:", error);
      toast.error(error.message || "Failed to cancel invitation");
    } finally {
      setIsRemovingMemberId(null);
    }
  };

  const handleInviteMember = async () => {
    if (!user.team || !user.team_id) return;

    if (!inviteEmail.trim() || !/^\S+@\S+\.\S+$/.test(inviteEmail)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setIsInvitingMember(true);
    try {
      const ownerProfile = teamData?.members.find((m) => m.role === "Owner") || null;
      await UserService.inviteMember(inviteEmail.trim(), user.team, ownerProfile, user.team.owner_id || user.id);
      toast.success(`Invitation sent to ${inviteEmail.trim()}`);
      setInviteEmail("");
      await refetchInvitations();
    } catch (error: any) {
      console.error("Error inviting team member:", error);
      toast.error(error.message || "Failed to send invitation");
    } finally {
      setIsInvitingMember(false);
    }
  };

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) {
      toast.error("Team name is required");
      return;
    }

    setIsSavingTeam(true);
    try {
      await ApiService.invoke({
        resource: "teams",
        action: "create",
        data: {
          name: newTeamName.trim(),
          brand: newTeamBrand.trim() || newTeamName.trim(),
          branding: brandingForm,
          impersonatedUserId: user.id,
        },
      });
      toast.success("Team created");
      setIsCreatingTeam(false);
      setNewTeamName("");
      setNewTeamBrand("");
      setBrandingForm(DEFAULT_BRANDING);
      onUserUpdated();
    } catch (error: any) {
      console.error("Error creating team:", error);
      toast.error(error.message || "Failed to create team");
    } finally {
      setIsSavingTeam(false);
    }
  };

  const handleStartEditTeam = () => {
    setEditTeamName(user.team?.name || "");
    setEditTeamBrand(user.team?.brand || "");
    setBrandingForm(branding || DEFAULT_BRANDING);
    setIsEditingTeam(true);
  };

  const handleSaveTeamEdits = async () => {
    if (!user.team_id) return;
    if (!editTeamName.trim()) {
      toast.error("Team name is required");
      return;
    }

    setIsSavingTeam(true);
    try {
      await ApiService.invoke({
        resource: "teams",
        action: "update",
        id: user.team_id,
        data: {
          name: editTeamName.trim(),
          brand: editTeamBrand.trim() || editTeamName.trim(),
        },
      });
      await AccountBrandingService.updateAccountBranding(user.team_id, brandingForm);
      queryClient.invalidateQueries({ queryKey: ["team-branding", user.team_id] });
      queryClient.invalidateQueries({ queryKey: ["account-branding", user.team_id] });
      toast.success("Team updated");
      setIsEditingTeam(false);
      onUserUpdated();
      await refetchTeam();
    } catch (error: any) {
      console.error("Error updating team:", error);
      toast.error(error.message || "Failed to update team");
    } finally {
      setIsSavingTeam(false);
    }
  };

  const handleDeleteTeam = async () => {
    if (!user.team_id) return;

    setIsDeletingTeam(true);
    try {
      await ApiService.invoke({
        resource: "teams",
        action: "delete",
        id: user.team_id,
      });
      toast.success("Team deleted");
      setIsDeleteDialogOpen(false);
      onUserUpdated();
    } catch (error: any) {
      console.error("Error deleting team:", error);
      toast.error(error.message || "Failed to delete team");
    } finally {
      setIsDeletingTeam(false);
    }
  };

  const handleChangeOwner = async () => {
    if (!user.team_id || !ownerCandidate) return;

    setIsChangingOwner(true);
    try {
      await ApiService.invoke({
        resource: "teams",
        action: "update",
        id: user.team_id,
        data: { owner_id: ownerCandidate.id },
      });
      toast.success(`${ownerCandidate.label} is now the team owner`);
      setOwnerCandidate(null);
      onUserUpdated();
      await refetchTeam();
    } catch (error: any) {
      console.error("Error changing team owner:", error);
      toast.error(error.message || "Failed to change team owner");
    } finally {
      setIsChangingOwner(false);
    }
  };

  const handleClearBranding = async () => {
    if (!teamId) return;

    setIsSavingBranding(true);
    try {
      await AccountBrandingService.clearAccountBranding(teamId);
      toast.success("Team branding cleared");
      queryClient.invalidateQueries({ queryKey: ["team-branding", teamId] });
      queryClient.invalidateQueries({ queryKey: ["account-branding", teamId] });
    } catch (error: any) {
      console.error("Error clearing team branding:", error);
      toast.error(error.message || "Failed to clear branding");
    } finally {
      setIsSavingBranding(false);
    }
  };

  const isOwner = user.team?.owner_id === user.id;

  return (
    <div className="flex flex-col gap-5">
      <div className="bg-white rounded-[10px] p-5 shadow-[0_3px_10px_rgba(0,0,0,0.05)]">
        <div className="flex items-center mb-[18px]">
          <div className="h-8 w-8 rounded-full bg-genie-primary/10 flex items-center justify-center mr-3">
            <Users className="h-5 w-5 text-genie-primary" />
          </div>
          <h2 className="text-xl font-semibold m-0">Current Team</h2>
        </div>

        {isTeamLoading ? (
          <p className="text-sm text-gray-500">Loading team...</p>
        ) : user.team ? (
          <div className="space-y-4">
            {isEditingTeam ? (
              <div className="p-3 bg-gray-50 rounded-md space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-team-name" className="block mb-1.5 text-xs font-medium">
                      Team Name
                    </Label>
                    <Input
                      id="edit-team-name"
                      value={editTeamName}
                      onChange={(e) => setEditTeamName(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-team-brand" className="block mb-1.5 text-xs font-medium">
                      Brand
                    </Label>
                    <Input
                      id="edit-team-brand"
                      value={editTeamBrand}
                      onChange={(e) => setEditTeamBrand(e.target.value)}
                    />
                  </div>
                </div>

                {isBrandingLoading ? (
                  <p className="text-sm text-gray-500">Loading branding...</p>
                ) : (
                  <BrandingFields
                    brandingForm={brandingForm}
                    setBrandingForm={setBrandingForm}
                    onClearBranding={branding ? handleClearBranding : undefined}
                    isSavingBranding={isSavingBranding}
                    onImpersonateToEdit={handleImpersonateForBranding}
                  />
                )}

                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setIsEditingTeam(false)} disabled={isSavingTeam}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleSaveTeamEdits}
                    disabled={isSavingTeam}
                    className="bg-genie-primary hover:bg-genie-primary/90"
                  >
                    {isSavingTeam && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between p-3 bg-gray-50 rounded-md">
                <div className="flex items-center gap-3 min-w-0">
                  {isBrandingLoading ? (
                    <div className="h-10 w-10 rounded-md bg-gray-200 flex-shrink-0 animate-pulse" />
                  ) : branding?.logoUrl ? (
                    <img
                      src={branding.logoUrl}
                      alt={`${user.team.name || "Team"} logo`}
                      className="h-10 w-10 rounded-md object-contain bg-white border border-gray-200 flex-shrink-0"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-md bg-gray-100 border border-dashed border-gray-300 flex items-center justify-center flex-shrink-0">
                      <Users className="h-4 w-4 text-gray-300" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900">{user.team.name || "Unnamed Team"}</div>
                    <div className="text-xs text-gray-500">Team ID: {user.team.id}</div>
                    {!isBrandingLoading && !branding?.logoUrl && (
                      <div className="text-xs text-gray-400 italic">No branding configured</div>
                    )}
                    {isOwner ? (
                      <Badge className="mt-1 text-genie-primary border-genie-primary/30 bg-genie-primary/5" variant="outline">
                        Owner
                      </Badge>
                    ) : (
                      <Badge className="mt-1 text-gray-600 border-gray-300" variant="outline">
                        Member
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleStartEditTeam}
                      className="text-gray-600 hover:text-gray-900"
                      title="Edit team"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsDeleteDialogOpen(true)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Delete team"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  {!isOwner && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveFromTeam}
                      disabled={updateUserMutation.isPending}
                      className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Remove user from this team"
                    >
                      <UserMinus className="h-3.5 w-3.5 mr-1.5" />
                      Remove from Team
                    </Button>
                  )}
                </div>
              </div>
            )}

            {isOwner && !isEditingTeam && (
              <p className="text-xs text-gray-500">
                This user owns the team. Deleting it will remove all members and pending invitations.
              </p>
            )}

            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Team Members ({(teamData?.members.length ?? 0) + (pendingInvitations?.length ?? 0)})
              </Label>
              {teamData?.members.length || pendingInvitations?.length ? (
                <div className="space-y-1.5">
                  {teamData?.members.map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                      <div className="min-w-0">
                        <span className="text-sm text-gray-800 truncate">
                          <CopyableEmail email={member.full_name || member.email || member.id} />
                        </span>
                        <Badge variant="outline" className="ml-2 text-xs">
                          {member.role}
                        </Badge>
                      </div>
                      {member.role !== "Owner" && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-gray-400 hover:text-genie-primary"
                            onClick={() =>
                              setOwnerCandidate({ id: member.id, label: member.full_name || member.email || member.id })
                            }
                            title="Make team owner"
                          >
                            <Crown className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-gray-400 hover:text-red-600"
                            onClick={() => handleRemoveMember(member.id, member.full_name || member.email || member.id)}
                            disabled={isRemovingMemberId === member.id}
                            title="Remove member"
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                  {pendingInvitations?.map((invitation) => (
                    <div key={invitation.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                      <div className="min-w-0">
                        <CopyableEmail email={invitation.email} className="text-sm text-gray-800" textClassName="truncate" />
                        <Badge variant="outline" className="ml-2 text-xs text-amber-600 border-amber-300">
                          Invited
                        </Badge>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 flex-shrink-0 text-gray-400 hover:text-red-600"
                        onClick={() => handleCancelInvitation(invitation.id, invitation.email)}
                        disabled={isRemovingMemberId === invitation.id}
                        title="Cancel invitation"
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No members found.</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="invite-email" className="text-sm font-medium">
                Invite New Member
              </Label>
              <div className="flex gap-2">
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="Enter email address"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleInviteMember}
                  disabled={isInvitingMember}
                  className="text-genie-primary border-genie-primary/30 hover:bg-genie-primary/10 flex-shrink-0"
                  title="Send Invite"
                >
                  {isInvitingMember ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
        ) : isCreatingTeam ? (
          <div className="space-y-3">
            <div>
              <Label htmlFor="new-team-name" className="block mb-1.5 text-sm font-medium">
                Team Name
              </Label>
              <Input
                id="new-team-name"
                placeholder="Enter team name"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="new-team-brand" className="block mb-1.5 text-sm font-medium">
                Brand
              </Label>
              <Input
                id="new-team-brand"
                placeholder="Defaults to team name"
                value={newTeamBrand}
                onChange={(e) => setNewTeamBrand(e.target.value)}
              />
            </div>

            <BrandingFields brandingForm={brandingForm} setBrandingForm={setBrandingForm} />

            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setIsCreatingTeam(false)} disabled={isSavingTeam}>
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleCreateTeam}
                disabled={isSavingTeam}
                className="bg-genie-primary hover:bg-genie-primary/90"
              >
                {isSavingTeam && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create Team
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">This user is not part of any team.</p>
            <Button type="button" variant="outline" size="sm" onClick={() => setIsCreatingTeam(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Team
            </Button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-[10px] p-5 shadow-[0_3px_10px_rgba(0,0,0,0.05)]">
        <div className="flex items-center mb-[18px]">
          <div className="h-8 w-8 rounded-full bg-genie-primary/10 flex items-center justify-center mr-3">
            <UserPlus className="h-5 w-5 text-genie-primary" />
          </div>
          <h2 className="text-xl font-semibold m-0">{user.team ? "Reassign Team" : "Assign to Team"}</h2>
        </div>

        <Label htmlFor="team-search" className="block mb-1.5 font-medium text-sm">
          Search Teams
        </Label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            id="team-search"
            placeholder="Search by team name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>

        {searchTerm && (
          <div className="mt-3 border border-gray-200 rounded-md divide-y max-h-64 overflow-y-auto">
            {isSearching ? (
              <p className="p-3 text-sm text-gray-500">Searching...</p>
            ) : teamsResult?.teams.length ? (
              teamsResult.teams.map((team) => (
                <div key={team.id} className="flex items-center justify-between p-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{team.name || "Unnamed Team"}</div>
                    <div className="text-xs text-gray-500 truncate">
                      Owner: <CopyableEmail email={team.owner?.full_name || team.owner?.email} fallback="Unknown" /> • {team.memberCount} member{team.memberCount === 1 ? "" : "s"}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleAssignTeam(team.id)}
                    disabled={updateUserMutation.isPending || team.id === user.team_id}
                    className="flex-shrink-0"
                  >
                    {team.id === user.team_id ? "Current" : "Assign"}
                  </Button>
                </div>
              ))
            ) : (
              <p className="p-3 text-sm text-gray-500">No teams found.</p>
            )}
          </div>
        )}
      </div>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg">Delete Team</AlertDialogTitle>
            <AlertDialogDescription className="text-sm mt-2">
              Are you sure you want to delete "{user.team?.name || "this team"}"? This will remove all members from the team and delete
              any pending invitations. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel className="mt-0" disabled={isDeletingTeam}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTeam}
              disabled={isDeletingTeam}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeletingTeam ? "Deleting..." : "Delete Team"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!ownerCandidate} onOpenChange={(open) => !open && setOwnerCandidate(null)}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg">Change Team Owner</AlertDialogTitle>
            <AlertDialogDescription className="text-sm mt-2">
              Make <span className="font-medium text-gray-900">{ownerCandidate?.label}</span> the owner of "{user.team?.name || "this team"}"?
              This transfers billing and subscription control for the whole team to this member, and the current owner will become a
              regular member.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel className="mt-0" disabled={isChangingOwner}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleChangeOwner}
              disabled={isChangingOwner}
              className="bg-genie-primary hover:bg-genie-primary/90 text-white"
            >
              {isChangingOwner ? "Changing..." : "Change Owner"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export interface BrandingFieldsProps {
  brandingForm: GenieBranding;
  setBrandingForm: (branding: GenieBranding) => void;
  onClearBranding?: () => void;
  isSavingBranding?: boolean;
  onImpersonateToEdit?: () => void;
}

export const BrandingFields = ({ brandingForm, setBrandingForm, onClearBranding, isSavingBranding, onImpersonateToEdit }: BrandingFieldsProps) => {
  return (
    <div className="space-y-4 border-t border-gray-200 pt-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ImageUpload
          bucket="genie-images"
          value={brandingForm.logoUrl || undefined}
          onChange={(url) => setBrandingForm({ ...brandingForm, logoUrl: url || null })}
          label="Team Logo"
        />

        <div className="space-y-4">
          <div>
            <Label htmlFor="branding-primary" className="block mb-1.5 text-xs font-medium">
              Primary Color
            </Label>
            <div className="flex items-center gap-2">
              <input
                id="branding-primary"
                type="color"
                value={brandingForm.primaryColor || "#4E9CFF"}
                onChange={(e) => setBrandingForm({ ...brandingForm, primaryColor: e.target.value })}
                className="h-9 w-12 rounded border border-gray-300 cursor-pointer"
              />
              <Input
                value={brandingForm.primaryColor || ""}
                onChange={(e) => setBrandingForm({ ...brandingForm, primaryColor: e.target.value })}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="branding-secondary" className="block mb-1.5 text-xs font-medium">
              Secondary Color
            </Label>
            <div className="flex items-center gap-2">
              <input
                id="branding-secondary"
                type="color"
                value={brandingForm.secondaryColor || "#FF4DB8"}
                onChange={(e) => setBrandingForm({ ...brandingForm, secondaryColor: e.target.value })}
                className="h-9 w-12 rounded border border-gray-300 cursor-pointer"
              />
              <Input
                placeholder="#FF4DB8"
                value={brandingForm.secondaryColor || ""}
                onChange={(e) => setBrandingForm({ ...brandingForm, secondaryColor: e.target.value || null })}
              />
            </div>
          </div>
          {onClearBranding && (
            <Button type="button" variant="outline" size="sm" onClick={onClearBranding} disabled={isSavingBranding}>
              Clear Branding
            </Button>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-gray-500 min-w-0">
          For full branding controls (gradients, QR/embed styling, etc.), use the account branding page.
        </p>
        {onImpersonateToEdit && (
          <Button type="button" variant="outline" size="sm" onClick={onImpersonateToEdit} className="flex-shrink-0">
            <LogIn className="h-3.5 w-3.5 mr-2" />
            Impersonate & Edit
          </Button>
        )}
      </div>
    </div>
  );
};
